import { useEffect, useState } from 'react';

import useResponsive from '../hooks/useResponsive';
import useCountry from '../hooks/useCountry';

import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Pagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';

import LoadingError from '../components/LoadingError';
import TopHeader from '../components/TopHeader';
import CompanyDetailSection from '../components/CompanyDetailSection';
import AboutCompanyCard from '../components/AboutCompanyCard';
import OurCommitmentCard from '../components/OurCommitmentCard';
import Footer from '../components/Footer';

import ReviewCard from '../components/ReviewCard';
import DummyReviewCard from '../components/DummyReviewCard';

import axios from '../utils/axios';
import OpeningHoursCard from '../components/OpeningHoursCard';
import ReviewFilterCard from '../components/ReviewFilterCard';
// ------------------------------------------------------------------------------------------

function ReviewsPage(props)
{
    let companyID = props?.companyID || null;
    const country = useCountry();
    const [ reviewsLoading, setReviewsLoading ] = useState(true);
    const [ companyLoading, setCompanyLoading ] = useState(true);
    const [ error, setError ] = useState(false);
    const [ reviews, setReviews ] = useState([]);
    const [ ratings, setRatings ] = useState('1,2,3,4,5');
    const [ company, setCompany ] = useState({});
    const [ currentPage, setCurrentPage ] = useState(1);
    const [ totalPages, setTotalPages ] = useState(1);
    const isDesktop = useResponsive('up', 'lg');

    useEffect( () => { 
        setCompanyLoading(true);
        setError(false);

        // Get company details
        axios.get(`/company/${companyID}?country=${country?.id}`)
        .then((response) => {             
            setCompany( response.data );
            setCompanyLoading(false);
            document.title = `Reviews of ${response?.data?.name}, ${response?.data?.address?.postcode?.toUpperCase()} | Working Feedback ${country?.iso_code}`;
            document.getElementById('canonical').setAttribute('rel', 'canonical');
            document.getElementById('canonical').setAttribute('href', `${response?.data?.country?.urls?.rpp}/${response?.data?.url_slug}`);
            document.getElementById('opengraph_title').setAttribute('content', `Reviews of ${response?.data?.name}, ${response?.data?.address?.postcode?.toUpperCase()}`);
            document.getElementById('opengraph_description').setAttribute('content', `A selection of reviews of ${response?.data?.name}, collated by Working Feedback - an impartial third party.`);
            document.getElementById('opengraph_image').setAttribute('content', `https://api9.workingfeedback.co.uk/company/${response?.data?.id}/social.jpg`);
            document.getElementById('snippets').innerHTML = `
            {
                "@context": "https://schema.org/",
                "@type": "AggregateRating",
                "itemReviewed": {
                    "@type": "LocalBusiness",
                    "name": ${JSON.stringify(response?.data?.name)},
                    "image": "https://icon-library.com/images/photo-placeholder-icon/photo-placeholder-icon-17.jpg",
                    "telephone": ${JSON.stringify(response?.data?.telephone)},
                    "priceRange": "££",
                    "address" : {
                    "@type": "PostalAddress",
                    "streetAddress": ${JSON.stringify(response?.data?.address?.line1)},
                    "addressLocality": ${JSON.stringify(response?.data?.address?.line2)},
                    "postalCode": ${JSON.stringify(response?.data?.address?.postcode)},
                    "addressCountry": ${JSON.stringify(country?.iso_code)}
                    }
                },
                "ratingValue": "${response?.data?.reviews?.stars}",
                "bestRating": "5",
                "worstRating": "1", 
                "ratingCount": "${response?.data?.reviews?.count}"
                }
            `;
            getReviews(1, ratings);
        })
        .catch((response) => { 
            console.warn(response);
            setCompanyLoading(false);
            setError('An unknown error occurred.');
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyID]);

    const getReviews = (page, ratings) => { 
        setReviewsLoading(true);
        setError(false);

        axios.get(`/company/${companyID}/reviews/feed?page=${page}`, { headers: { 'page': page, 'ratings': ratings, 'perPage': 10 } })
        .then((response) => { 
            setReviews( response.data?.reviews );
            setCurrentPage( response.data?.meta?.current_page );
            setTotalPages( response.data?.meta?.last_page );
            setReviewsLoading(false);
            console.log(response.data?.meta);
        })
        .catch((response) => { 
            console.warn(response);
            setReviewsLoading(false);
            setCurrentPage(1);
            setTotalPages(1);
            setError('An unknown error occurred.');
        });
    }

    const handlePaginationChange = (e, value) => { 
        setCurrentPage(value);
        getReviews(value, ratings);
    }

    const handleRatingsChange = (newValues) => { 
        setRatings(newValues);
        setCurrentPage(1);
        getReviews(1, newValues);
    }
    
    return ( 
        <>
            <TopHeader companyID={props?.companyID} />
            <CompanyDetailSection loading={companyLoading} error={error} company={company}/>

            <Container sx={{ marginTop: error ? '30px' : '0px' }}>
                <Grid container spacing={5}>

                    { /* Left column (main page content) */ }
                    <Grid item xs={12} sm={12} md={8} lg={8}>

                        {!isDesktop && !error && ( 
                            <Box sx={{ marginBottom: '30px' }}>
                                <ReviewFilterCard loading={reviewsLoading} setRatings={(v) => handleRatingsChange(v)} />
                            </Box>
                        )}


                        <Typography variant="h5" component="h2" mb={4}>Latest Reviews by Working Feedback</Typography>  

                        { error && ( <LoadingError /> ) }

                        { reviewsLoading && !error && ( 
                            <Box sx={{ textAlign: 'center', marginBottom: '50px' }}>
                                <DummyReviewCard />
                                <DummyReviewCard />
                                <DummyReviewCard />
                                <DummyReviewCard />
                                <DummyReviewCard />
                                <DummyReviewCard />
                            </Box>
                        ) }

                        { !reviewsLoading && !error && ( 
                            <Stack spacing={3} mb={6}>
                                { reviews?.length > 0 && reviews?.map((review,i) => (                                 
                                    <ReviewCard key={i} review={review} displayCompanyName={false} />
                                )) }

                                { reviews?.length === 0 && ( 
                                    <Typography variant="body2" mb={2}>There were no reviews to display matching the currently selected filters.</Typography>
                                )}
                            </Stack>
                        )}
                        
                        { !error && !reviewsLoading && ( 
                        <Grid container spacing={5}>
                            <Grid item xs={12}>
                                <Pagination 
                                    count={totalPages} 
                                    page={currentPage} 
                                    onChange={(e, value) => handlePaginationChange(e, value)} 
                                    showLastButton 
                                    boundaryCount={1} 
                                    siblingCount={1} 
                                    color="secondary" 
                                    sx={{ display:'flex', justifyContent:'center', alignItems:'center', marginBottom: '50px' }} 
                                />
                            </Grid>
                        </Grid>
                        )}

                    </Grid>

                    { /* Sidebar */ }
                    <Grid item xs={12} sm={12} md={4} lg={4}>
                        <Stack spacing={3}>
                            {isDesktop && !error && ( 
                                <ReviewFilterCard loading={reviewsLoading} setRatings={(v) => handleRatingsChange(v)} />
                            )}
                            <OpeningHoursCard error={error} loading={companyLoading} openingHours={company.opening_hours} />
                            <AboutCompanyCard error={error} loading={companyLoading} company={company} />
                            <OurCommitmentCard />
                        </Stack>
                    </Grid>
                </Grid>
                                  
            </Container>
            <Footer />
        </>
    )
}

export default ReviewsPage;